import { Url, UUID } from '@hc-frontend/core-types';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import { z } from 'zod';

import {
  BLANK,
  GenderType,
  PartialSurveyAnswers,
  PlateName,
  QuestionWithThirdPartyFields,
} from '../../domain';
import type { ExtractSurveyAnswersRepository } from '../../domain/repositories/extract-survey-answers-repository';

const SpecialCaseQuestions = QuestionWithThirdPartyFields.extract([
  'DATE_OF_BIRTH',
  'GENDER',
  'xxTrustedFormCertUrl',
  'universal_leadid',
  'ASK_FOR_ZIP_CODE',
]);
type SpecialCaseQuestions = z.infer<typeof SpecialCaseQuestions>;

function parseDateOfBirth(
  valueToParse: FormDataEntryValue,
): PartialSurveyAnswers {
  if (typeof valueToParse === 'string' && valueToParse === '') {
    return { DATE_OF_BIRTH: BLANK.value };
  }

  const CoerceDate = z.coerce.date();
  const parsedDate = CoerceDate.safeParse(valueToParse);
  if (!parsedDate.success) {
    throw parsedDate.error;
  }

  return { DATE_OF_BIRTH: parsedDate.data };
}

function parseGender(valueToParse: FormDataEntryValue): PartialSurveyAnswers {
  if (typeof valueToParse === 'string' && valueToParse === '') {
    return { GENDER: BLANK.value };
  }

  const parsedGender = GenderType.safeParse(valueToParse);
  if (!parsedGender.success) {
    throw parsedGender.error;
  }

  return { GENDER: parsedGender.data };
}

function parseUniversalLeadId(
  valueToParse: FormDataEntryValue,
): PartialSurveyAnswers {
  const parsedIdentifier = UUID.safeParse(valueToParse);
  if (!parsedIdentifier.success) {
    throw parsedIdentifier.error;
  }

  return { LEAD_ID: parsedIdentifier.data };
}

function parseTrustedFormCertificateUrl(
  valueToParse: FormDataEntryValue,
): PartialSurveyAnswers {
  const parsedUrl = Url.safeParse(valueToParse);
  if (!parsedUrl.success) {
    throw parsedUrl.error;
  }

  return { TRUSTED_FORM_URL: parsedUrl.data };
}

function parseAskForZipCode(
  valueToParse: FormDataEntryValue,
): PartialSurveyAnswers {
  return { ASK_FOR_ZIP_CODE: valueToParse === 'true' };
}

type QuestionValidation = (
  valueToParse: FormDataEntryValue,
) => PartialSurveyAnswers;
const questionsValidationMap: Record<SpecialCaseQuestions, QuestionValidation> =
  {
    DATE_OF_BIRTH: parseDateOfBirth,
    GENDER: parseGender,
    universal_leadid: parseUniversalLeadId,
    xxTrustedFormCertUrl: parseTrustedFormCertificateUrl,
    ASK_FOR_ZIP_CODE: parseAskForZipCode,
  };

function buildQuestionAnswer(
  question: string,
  answer: FormDataEntryValue,
): PartialSurveyAnswers {
  const parsedQuestion = QuestionWithThirdPartyFields.safeParse(question);
  if (!parsedQuestion.success) {
    throw new Error(`The question "${question}" is not valid.`);
  }

  if (parsedQuestion.data in SpecialCaseQuestions.Values) {
    const parsedSpecialKey = SpecialCaseQuestions.parse(parsedQuestion.data);
    return questionsValidationMap[parsedSpecialKey](answer);
  }

  return {
    [parsedQuestion.data]: answer,
  };
}

const PlateNameWithSkip = PlateName.extract([
  'COVERAGE_URGENCY',
  'ENROLLED_PART_A_AND_PART_B',
  'GENDER_AND_DATE_OF_BIRTH',
]);
type PlateNameWithSkip = z.infer<typeof PlateNameWithSkip>;

const emptyAnswerForPlate: Record<PlateNameWithSkip, PartialSurveyAnswers> = {
  COVERAGE_URGENCY: {
    WHEN_YOU_NEED_MEDICARE_COVERAGE: BLANK.value,
  },
  ENROLLED_PART_A_AND_PART_B: {
    ARE_YOU_CURRENTLY_ENROLLED_IN_MEDICARE: BLANK.value,
  },
  GENDER_AND_DATE_OF_BIRTH: {
    GENDER: BLANK.value,
    DATE_OF_BIRTH: BLANK.value,
  },
};

function getEmptyAnswersForPlate(plateName: PlateName) {
  const parsedPlateNameWithSkip = PlateNameWithSkip.safeParse(plateName);
  if (!parsedPlateNameWithSkip.success) {
    throw new Error(
      `No answers were found, but the plate "${plateName}" should not be skipped.`,
    );
  }

  return emptyAnswerForPlate[parsedPlateNameWithSkip.data];
}

export function extractSurveyAnswersAdapterFactory(): ExtractSurveyAnswersRepository {
  return {
    extractSurveyAnswers: async ({ rawSurveyAnswers, plateName }) => {
      try {
        let surveyAnswers: PartialSurveyAnswers = {};
        let answerCount = 0;

        rawSurveyAnswers.forEach((value, key) => {
          answerCount += 1;
          surveyAnswers = {
            ...surveyAnswers,
            ...buildQuestionAnswer(key, value),
          };
        });

        const answersToUse =
          answerCount === 0
            ? getEmptyAnswersForPlate(plateName)
            : PartialSurveyAnswers.parse(surveyAnswers);

        return createSuccessOperationResult(answersToUse);
      } catch (error) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToExtractSurveyAnswers',
              rootCause: error as Error,
            },
          ],
        });
      }
    },
  };
}
